import http from "@/http";
import store from "@/store";

// Finance Reports API wrapper (region/district budget-execution figures).
// Spec paths are written as `/v1/...`; in this console every endpoint lives
// under `api/v1/...` (e.g. the picker endpoints `/v1/regions` are already used
// here as `api/v1/regions`), so the real base is `api/v1/finance-reports`.
const BASE = "api/v1/finance-reports";

// The console sends no global `hl` (language) header and has no i18n layer —
// the whole UI is Uzbek-Latin. We set `hl` explicitly on finance requests so
// region/district titles come back in Latin instead of the backend's default
// (uz_cyr) fallback (spec §2.10).
export const FINANCE_HL = "uz_lat";

// Drop empty params so we land in the right GET filter mode (spec §2.4):
// omitting `quarter` = annual totals, omitting `year` = full history.
function pruneParams(params = {}) {
  const out = {};
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value !== null && value !== undefined && value !== "") out[key] = value;
  });
  return out;
}

function cfg({ params, responseType } = {}) {
  const config = { headers: { hl: FINANCE_HL } };
  if (params) config.params = pruneParams(params);
  if (responseType) config.responseType = responseType;
  return config;
}

export default {
  // ---- Region reports ----
  listRegions(params) {
    return http.get(`${BASE}/regions`, cfg({ params }));
  },
  createRegion(body) {
    return http.post(`${BASE}/regions`, body, cfg());
  },
  updateRegion(id, body) {
    return http.put(`${BASE}/regions/${id}`, body, cfg());
  },
  deleteRegion(id) {
    return http.delete(`${BASE}/regions/${id}`, cfg());
  },

  // ---- District reports ----
  listDistricts(params) {
    return http.get(`${BASE}/districts`, cfg({ params }));
  },
  createDistrict(body) {
    return http.post(`${BASE}/districts`, body, cfg());
  },
  updateDistrict(id, body) {
    return http.put(`${BASE}/districts/${id}`, body, cfg());
  },
  deleteDistrict(id) {
    return http.delete(`${BASE}/districts/${id}`, cfg());
  },

  // ---- Excel export (binary) ----
  exportExcel(params) {
    return http.get(`${BASE}/excel`, cfg({ params, responseType: "blob" }));
  },

  // ---- Pickers (pre-existing shared endpoints) ----
  listRegionPicker() {
    return http.get("api/v1/regions", cfg());
  },
  listDistrictPicker(region_id) {
    return http.get("api/v1/districts", cfg({ params: { region_id } }));
  },
  listRegionsWithSoato() {
    return http.get("api/v1/regions-with-soato", cfg());
  },
};

// ---- Excel upload ----
// Multipart uploads go through el-upload (native XHR), not the shared axios
// instance, because that instance's transformRequest JSON-stringifies every
// body (it would corrupt FormData) — same pattern the rest of the console uses
// for uploads. These helpers keep the URL/headers consistent with the above.
export function uploadUrl(year, quarter) {
  return `${process.env.VUE_APP_BASE_API_URL}${BASE}/upload?year=${year}&quarter=${quarter}`;
}

export function uploadHeaders() {
  return { Authorization: `${store.state.accessToken}`, hl: FINANCE_HL };
}
